import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import appSettings from '../../appSettings';
import subbrandsLogo from '../../images/subbrands.png';
import { BetaFeature } from '../../@types/BetaFeature.type';
import { BETA_VERSION } from '../betaConfig';
import ThemeToggle from './ThemeToggle';

interface BetaHeaderProps {
  userName?: string;
  userEmail?: string;
  pageTitle: string;
  features: BetaFeature[];
  onLogout?: () => void;
}

export const HELP_DOC_URL = '/docs/JetstreamBetaHelp.docx';

export const getInitials = (name?: string): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? (parts.at(-1) ?? '').charAt(0) : '';
  return `${first}${last}`.toUpperCase();
};

const MoreIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

const LogoutIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 14H3.333A1.333 1.333 0 0 1 2 12.667V3.333A1.333 1.333 0 0 1 3.333 2H6M10.667 11.333L14 8l-3.333-3.333M14 8H6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HelpIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
  </svg>
);

const BetaHeader: React.FC<BetaHeaderProps> = ({
  userName,
  userEmail,
  pageTitle,
  features,
  onLogout,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const displayName = userName?.trim() || 'User';
  const initials = getInitials(userName);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  return (
    <header className="beta-header" data-testid="beta-header">
      <div className="beta-header__bar">
        <div className="beta-header__brand">
          <p className="beta-header__title">JetStream</p>
          <img
            className="beta-header__subbrands"
            src={subbrandsLogo}
            alt="Alaska Hawaiian Horizon"
          />
        </div>

        <div className="beta-header__right" ref={menuRef}>
          <div className="beta-header__user-inline">
            <span className="beta-header__username">{displayName}</span>
            <span className="beta-header__separator" aria-hidden="true">
              |
            </span>
            <span className="beta-header__meta-item" data-testid="beta-version">
              V{BETA_VERSION}
            </span>
            <span className="beta-header__separator" aria-hidden="true">
              |
            </span>
            <a
              className="beta-header__help-link"
              href={HELP_DOC_URL}
              download
              data-testid="beta-help-link"
            >
              Help
            </a>
            <span className="beta-header__separator" aria-hidden="true">
              |
            </span>
            <span className="beta-header__meta-item" data-testid="beta-env">
              {appSettings.ENV}
            </span>
          </div>

          <ThemeToggle />

          <button
            type="button"
            className={`beta-header__menu-button${menuOpen ? ' is-open' : ''}`}
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Open user menu"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid="user-menu-button"
          >
            <MoreIcon />
          </button>

          {menuOpen && (
            <div
              className="beta-header__menu"
              role="menu"
              data-testid="user-menu"
            >
              <div className="beta-header__menu-header">
                <span className="beta-header__menu-avatar" aria-hidden="true">
                  {initials}
                </span>
                <div className="beta-header__menu-info">
                  <span className="beta-header__menu-name">{displayName}</span>
                  <span className="beta-header__menu-secondary">
                    V{BETA_VERSION} · {appSettings.ENV}
                  </span>
                  {userEmail && (
                    <span className="beta-header__menu-secondary">
                      {userEmail}
                    </span>
                  )}
                </div>
              </div>
              <div className="beta-header__menu-divider" />
              <a
                className="beta-header__menu-item beta-header__menu-item--compact"
                href={HELP_DOC_URL}
                download
                role="menuitem"
                onClick={() => setMenuOpen(false)}
              >
                <HelpIcon />
                Help
              </a>
              {onLogout && (
                <button
                  type="button"
                  className="beta-header__menu-item beta-header__menu-item--logout"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout();
                  }}
                  data-testid="logout-button"
                >
                  <LogoutIcon />
                  Logout
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="beta-header__page">
        <h1 className="beta-header__page-title">{pageTitle}</h1>
        {features.length > 1 && (
          <nav className="beta-header__nav" aria-label="Beta features">
            {features.map((feature) => (
              <NavLink key={feature.id} to={feature.path}>
                {feature.label}
              </NavLink>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default BetaHeader;
